import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Send } from "lucide-react";
import { api, ApiError } from "../api/client";

export interface PeerStatus {
  /** The colleague feedback id; needed to resend the request. */
  id?: number;
  name: string;
  status: string;
  overdue: boolean;
  deadline: string | null;
  invited: boolean;
}

const DATE_FMT = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" });

/**
 * Who is giving colleague feedback on a review, and where each one is:
 * green with a tick = submitted, grey = waiting (with due date), red =
 * overdue, "invite not sent yet" when the review hasn't been triggered.
 * Shown only to the review's manager and admins — never to the subject.
 */
export default function PeerStatusChips({
  peers,
  emptyText,
  canResend = false,
}: {
  peers: PeerStatus[];
  emptyText?: string;
  /** Show "Resend" beside colleagues who were asked and haven't replied. */
  canResend?: boolean;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<number | null>(null);
  const [notes, setNotes] = useState<Record<number, { ok: boolean; text: string }>>({});

  async function resend(p: PeerStatus) {
    if (p.id == null) return;
    setBusy(p.id);
    try {
      const r = await api.post<{ sent: boolean; email_paused: boolean; message: string }>(
        `/api/reviews/colleague-feedback/${p.id}/resend`,
      );
      setNotes((n) => ({ ...n, [p.id as number]: { ok: r.sent, text: r.message } }));
    } catch (e) {
      setNotes((n) => ({
        ...n,
        [p.id as number]: { ok: false, text: e instanceof ApiError ? e.message : t("team.resendFailed", "Couldn't resend. Try again.") },
      }));
    } finally {
      setBusy(null);
    }
  }

  if (peers.length === 0) {
    return (
      <p className="mt-1 text-xs text-gray-400">
        {emptyText ?? t("team.noColleaguesYet", "No colleagues chosen yet.")}
      </p>
    );
  }
  return (
    <div className="mt-1 flex flex-wrap gap-1.5">
      {peers.map((p) => (
        <span
          key={p.id ?? p.name}
          className={[
            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
            p.status === "submitted"
              ? "border-green-200 bg-green-50 text-green-700"
              : p.overdue
                ? "border-red-200 bg-red-50 text-red-700"
                : "border-gray-200 bg-gray-50 text-gray-600",
          ].join(" ")}
        >
          {p.status === "submitted" && <CheckCircle2 size={11} strokeWidth={2} aria-hidden />}
          {p.name}
          {p.status !== "submitted" && (
            <span className={p.overdue ? "" : "text-gray-400"}>
              {!p.invited
                ? `· ${t("team.peerNotInvited", "invite not sent yet")}`
                : p.overdue
                  ? `· ${t("team.peerOverdue", "overdue")}`
                  : p.deadline
                    ? `· ${t("team.peerDue", "due {{date}}", { date: DATE_FMT.format(new Date(p.deadline)) })}`
                    : ""}
            </span>
          )}
          {canResend && p.id != null && p.invited && p.status !== "submitted" && (
            <button
              type="button"
              disabled={busy === p.id}
              onClick={() => void resend(p)}
              title={t("team.resendTitle", "Send {{name}} the feedback request again", { name: p.name })}
              className="ml-0.5 inline-flex items-center gap-0.5 rounded-full border border-pme-blue/30 bg-white px-1.5 py-px text-[10px] font-semibold text-pme-blue hover:bg-pme-sky disabled:opacity-50"
            >
              <Send size={10} aria-hidden />
              {busy === p.id ? t("team.resending", "Sending…") : t("team.resend", "Resend")}
            </button>
          )}
        </span>
      ))}
      {Object.entries(notes).map(([id, n]) => (
        <p key={id} className={`w-full text-[11px] ${n.ok ? "text-green-700" : "text-amber-700"}`} aria-live="polite">
          {n.text}
        </p>
      ))}
    </div>
  );
}
